import { ImageLabelLineProps, MultiSelectOption, ObjectOption } from 'components/shared'
import { SelectOption } from 'components/shared/Select'
import { action, computed, observable, toJS } from 'mobx'
import { computedFn } from 'mobx-utils'
import {
  AgreementDTO,
  AgreementItemPropertyTypes,
  AgreementItemTypes,
  AgreementScopeDTO,
  AgreementScopeFilterDTO,
  AgreementStatusTypes,
  Brand,
  FilterTypes,
  Product,
  ProjectDTO
} from 'models'
import { OnSelectCallbackParam } from 'react-daterange-picker'
import { BRAND_STORE, RootStore } from 'store'
import { PROJECT_STORE } from 'store/ProjectStore'
import { getMainImageSrcForObj, User } from 'utilities'
import {
  defaultScope, DiffMapDeepResult,
  ObjectValueDiff, ObjectValueStatus,
  VersionChange
} from './AgreementPageStore.types'
import { AGREEMENT_STORE } from 'store/AgreementStore'
import * as Moment from 'moment'
import { extendMoment } from 'moment-range'
import { EntityStatusTypes } from '../../models/EntityStatusTypes'
import { DeepDiffMapper, getAgreementPropLabel } from './AgreementPageStore.utils'
import { capitalize } from 'validate.js'

const moment = extendMoment(Moment)

export const AGREEMENT_PAGE_STORE = 'AGREEMENT_PAGE_STORE'

export class AgreementPageStore {
  private _rootStore: RootStore

  constructor (rootStore: RootStore) {
    this._rootStore = rootStore
  }

  @observable agreement: AgreementDTO = null
  @observable private areAllChangesAccepted: boolean = false
  @observable private versionChangeKeyIdsToSkip: string[] = []
  private _hasChanges: boolean = false

  @action setAgreement = (agreement: AgreementDTO) => {
    this.agreement = agreement
  }

  @action private setAreAllChangesAccepted = (value: boolean) => {
    this.areAllChangesAccepted = value
  }

  @action private addVersionChangeKeyIdToSkip = (keyId: string) => {
    this.versionChangeKeyIdsToSkip.push(keyId)
  }

  @action private cleatVersionChangeKeyIds = () => {
    this.versionChangeKeyIdsToSkip = []
  }

  private get _scope (): AgreementScopeDTO {
    if (!this.agreement || !this.agreement.scopes.length) return defaultScope
    return this.agreement.scopes[0] || defaultScope
  }

  public get scopeBrand (): Brand {
    const filter = this._getFilterByType(FilterTypes.BRANDS)
    if (!filter || !filter.brands || !filter.brands.length) return null

    return filter.brands[0] || null
  }

  public get scopeProjects (): ProjectDTO[] {
    const filter = this._getFilterByType(FilterTypes.PROJECTS)
    if (!filter) return []

    return filter.projects || []
  }

  public get scopeProducts (): Product[] {
    const filter = this._getFilterByType(FilterTypes.PRODUCTS)
    if (!filter) return []

    return filter.products || []
  }

  private get _brandProducts (): Product[] {
    const { brandToView } = this._rootStore[BRAND_STORE]

    if (!brandToView || !brandToView.products || !brandToView.products.length) return []
    return brandToView.products
  }

  private get _projectsList (): ProjectDTO[] {
    const { projectsList } = this._rootStore[PROJECT_STORE]
    if (!projectsList || !projectsList.length) return []
    return projectsList.map(project => project.asDTO)
  }

  private _setScopeFilters = (filters: AgreementScopeFilterDTO[]) => {
    const scope: AgreementScopeDTO = {
      ...this._scope,
      filters
    }
    this.setAgreementProps({ scopes: [scope] })
  }

  private _parseToObjectOptions = (list: any[]): ObjectOption[] => {
    if (!list || !list.length) return []

    return list.map(e => ({
      id: e.id,
      imageSrc: getMainImageSrcForObj(e),
      label: e.name,
      versionChange: this.getChangeByKeyId(`product-${e.id}`)
    }))
  }

  private _getFilterIndexByType = (type: FilterTypes): number => {
    if (!this.agreement) return -1

    if (!this._scope) return -1

    return this._scope.filters.findIndex(f => f.type === type)
  }

  private _getFilterByType = (type: FilterTypes): AgreementScopeFilterDTO => {
    const filterIndex = this._getFilterIndexByType(type)

    if (filterIndex < 0) return null

    return this._scope.filters[filterIndex]
  }

  private _getProductById = (id: number): Product => {
    return this._brandProducts.find(p => p.id === id)
  }

  private _isProductSelected = (productId: number): boolean => {
    const isSelected = Boolean(this.scopeProducts.find(p => p.id === productId))
    return isSelected
  }

  private _isProjectSelected = (projectId: number, projectList = this.scopeProjects): boolean => {
    for (const project of projectList) {
      if (project.id === projectId) {
        return true
      }
      if (project.children && this._isProjectSelected(projectId, project.children)) {
        return true
      }
    }
    return false
  }

  private _getProjectById = (projectId: number, projectList = this._projectsList): ProjectDTO | null => {
    for (const project of projectList) {
      if (project.id === projectId) {
        return project
      }
      if (project.children) {
        const found = this._getProjectById(projectId, project.children)
        if (found) {
          return found
        }
      }
    }
    return null
  }

  clearStore = () => {
    this.setAgreement(null)
    this.setAreAllChangesAccepted(false)
    this.cleatVersionChangeKeyIds()
    this._hasChanges = false
  }

  setAgreementProps = (props: Partial<AgreementDTO>) => {
    if (!this._hasChanges) {
      this._hasChanges = true
    }
    this.setAgreement({
      ...this.agreement || {} as AgreementDTO,
      ...props
    })
  }

  setAgreementItemProp = (type: AgreementItemTypes, prop: AgreementItemPropertyTypes) =>
    (value: number) => {
      if (!this.agreement || !this.agreement.items) return

      const items = [...this.agreement.items]
      const itemIndex = items.findIndex(item => item.type === type)

      if (itemIndex === -1) return

      items[itemIndex].properties[prop] = value
      this.setAgreementProps({ items })
    }

  startNewAgreement = () => {
    this.setAgreement({
      budget: 0,
      budgetBuffer: 0,
      budgetRemaining: null,
      budgetSpent: null,
      createdDate: null,
      effectOnElements: false,
      endDate: null,
      externalId: null,
      id: null,
      images: [],
      info: '',
      items: [
        {
          id: null,
          properties: {
            [AgreementItemPropertyTypes.BLOB_TRIGGERED]: 0,
            [AgreementItemPropertyTypes.MAX_ATTACH_TIME_SECONDS]: 0,
            [AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_ATTACHED]: 0,
            [AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_DETACHED]: 0,
            [AgreementItemPropertyTypes.MINIMUM_TIME_IN_STACK]: 0,
            [AgreementItemPropertyTypes.MAXIMUM_TIME_IN_STACK]: 0
          },
          type: AgreementItemTypes.BLOB_SETTINGS
        },
        {
          id: null,
          properties: {
            [AgreementItemPropertyTypes.PRODUCT_PAGE_OPENED_FROM_BLOB]: 0,
            [AgreementItemPropertyTypes.PRODUCT_ADDED_TO_CART]: 0
          },
          type: AgreementItemTypes.PRODUCT
        },
        {
          id: null,
          properties: {
            [AgreementItemPropertyTypes.PRICE_PER_VIDEO_PLAYBACK]: 0
          },
          type: AgreementItemTypes.VIDEO_COMMERCIAL
        },
        {
          id: null,
          properties: {
            [AgreementItemPropertyTypes.PRICE_PER_CLICK]: 0,
            [AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN]: 0
          },
          type: AgreementItemTypes.POSTER
        },
        {
          id: null,
          properties: {
            [AgreementItemPropertyTypes.PURCHASE_FEE]: 0
          },
          type: AgreementItemTypes.PRODUCT_PURCHASE
        }
      ],
      loadContents: null,
      mainImage: null,
      name: '',
      organizationId: null,
      ownerId: null,
      relatedMedia: [],
      scopes: [defaultScope],
      startDate: null,
      status: EntityStatusTypes.NEW,
      agreementStatus: AgreementStatusTypes.NEW,
      tags: null
    })
    this._hasChanges = true
    this._rootStore[BRAND_STORE].getBrands()
    this._rootStore[PROJECT_STORE].getProjects(true, true)
  }

  onStoreSelect = (brandId: number) => {
    this._rootStore[BRAND_STORE].getBrandById(brandId)
      .then(() => {
        const filterIndex = this._getFilterIndexByType(FilterTypes.BRANDS)
        const filters = [...this._scope.filters]
        filters[filterIndex].brands = [this._rootStore[BRAND_STORE].brandToView]
        this._setScopeFilters(filters)
      })
  }

onProductSelect = (productId: number) => {
  const filterIndex = this._getFilterIndexByType(FilterTypes.PRODUCTS)
  const filters = [...this._scope.filters]

  if (filterIndex < 0) {
    filters.push({
      id: null,
      budget: null,
      type: FilterTypes.PRODUCTS,
      brands: [],
      products: [this._getProductById(productId)],
      projects: [],
      values: null
    })
  } else {
    const productIndex = filters[filterIndex].products.findIndex(p => p.id === productId)
    if (productIndex > -1) {
      filters[filterIndex].products.splice(productIndex, 1)
    } else {
      filters[filterIndex].products.push(this._getProductById(productId))
    }
  }
  this._setScopeFilters(filters)
}

  onProjectSelect = (projectId: number) => {
    const filterIndex = this._getFilterIndexByType(FilterTypes.PROJECTS)
    const filters = [...this._scope.filters]

    if (filterIndex < 0) {
      filters.push({
        id: null,
        budget: null,
        type: FilterTypes.PROJECTS,
        brands: [],
        products: [],
        projects: [this._getProjectById(projectId)],
        values: null
      })
    } else {
      this._isProjectSelected(projectId)
        ? filters[filterIndex].projects = filters[filterIndex].projects
          .filter(p => p.id !== projectId)
        : filters[filterIndex].projects.push(this._getProjectById(projectId))
    }
    this._setScopeFilters(filters)
  }

  onSelectAllProducts = () => {
    const filterIndex = this._getFilterIndexByType(FilterTypes.PRODUCTS)
    const filters = [...this._scope.filters]

    if (filterIndex < 0) {
      filters.push({
        id: null,
        budget: null,
        type: FilterTypes.PRODUCTS,
        brands: [],
        products: [...this._brandProducts],
        projects: [],
        values: null
      })
    } else {
      const areAllSelected = this._brandProducts.length === this.selectedProductsList.length
      areAllSelected
        ? filters[filterIndex].products = []
        : filters[filterIndex].products = [...this._brandProducts]
    }

    this._setScopeFilters(filters)
  }

  onSelectedProjectsChange = (listIds: number[]) => {
    const projects = listIds.map((id) => this._getProjectById(id))
    const filterIndex = this._getFilterIndexByType(FilterTypes.PROJECTS)
    const filters = [...this._scope.filters]

    if (filterIndex < 0) {
      filters.push({
        id: null,
        budget: null,
        type: FilterTypes.PROJECTS,
        brands: [],
        products: [],
        projects,
        values: null
      })
    } else {
      filters[filterIndex].projects = projects
    }

    this._setScopeFilters(filters)
  }

  onPeriodChange = (period: OnSelectCallbackParam) => {
    const startDate = period ? period.start.valueOf() : null
    const endDate = period ? period.end.valueOf() : null

    this.setAgreementProps({ startDate, endDate })
  }

  createAgreement = async () => {
    if (!this.isAgreementReadyToSave) return
    this._rootStore[AGREEMENT_STORE].createAgreement(this.agreement)
  }

  updateAgreement = async () => {
    // if (!this.isAgreementReadyToSave) return
    const modified = this.agreement.scopes.map(scope => {
      const modifiedFilters = scope.filters.map(filter => {
        const productsWithAscShipping = filter.products?.map(product => {
          return {
            ...product,
            shippings: product.shippings.sort((a, b) => a.id - b.id)
          }
        })
        return { ...filter, products: productsWithAscShipping }
      })
      return { ...scope, filters: modifiedFilters }
    })

    const agreementWithAscProductShippings = { ...this.agreement, scopes: modified } as AgreementDTO

    this._rootStore[AGREEMENT_STORE].editAgreement(agreementWithAscProductShippings)
  }

  updateAgreementStatus = async () => {
    this._rootStore[AGREEMENT_STORE].updateAgreementStatus(this.agreement.id, this.agreement.agreementStatus)
  }

  getAgreementById = (id: number | string) => {
    this._rootStore[AGREEMENT_STORE]
      .getAgreementById(id)
      .then(data => {
        this.setAgreement(data)
        this.scopeBrand
          ? this._rootStore[BRAND_STORE].getBrandById(this.scopeBrand.id, true, false)
          : this._rootStore[BRAND_STORE].getBrands()
        this._rootStore[PROJECT_STORE].getProjects(true, true)
      })
  }

  public onAcceptAllChangesClick = () => {
    this.setAreAllChangesAccepted(true)
  }

  public onConfirmAgreementClick = () => {
    this.setAgreementProps({ agreementStatus: AgreementStatusTypes.AGREED })
    this.updateAgreementStatus()
  }

  public onRejectChangeClick = (versionChange: VersionChange) => {
    this.addVersionChangeKeyIdToSkip(versionChange.keyId)

    if (versionChange.keyId.startsWith('product')) {
      if (versionChange.valueAfterChange) {
        this.onProductSelect((versionChange.valueAfterChange as any).id)
      }
      if (versionChange.valueBeforeChange) {
        this.onProductSelect((versionChange.valueBeforeChange as any).id)
      }
    } else if (versionChange.keyId.startsWith('project')) {
      if (versionChange.valueAfterChange) {
        this.onProjectSelect((versionChange.valueAfterChange as any).id)
      }
      if (versionChange.valueBeforeChange) {
        this.onProjectSelect((versionChange.valueBeforeChange as any).id)
      }
    } else {
      this.setAgreementProps({ [versionChange.keyId]: versionChange.valueBeforeChange })
    }
  }

  public onAcceptChangeClick = (versionChange: VersionChange) => {
    this.addVersionChangeKeyIdToSkip(versionChange.keyId)
  }

  @computed get isAgreementReadyToSave (): boolean {
    if (!this.agreement) return false

    const {
      name,
      startDate,
      endDate
    } = this.agreement

    if (
      !name ||
      !startDate ||
      !endDate ||
      !this.scopeBrand ||
      !this._hasChanges
    ) return false

    return true
  }

  @computed get isEditing (): boolean {
    return Boolean(this.agreement && this.agreement.id)
  }

  @computed get navLabel (): string {
    if (!this.isEditing) {
      return 'Add new'
    }

    return this.agreement.name
  }

  @observable getItemProp = computedFn(
    (type: AgreementItemTypes, prop: AgreementItemPropertyTypes): number => {
      if (!this.agreement || !this.agreement.items) return null

      const item = this.agreement.items.find(itm => itm.type === type)
      if (!item) return null
      return item.properties[prop]
    }
  )

  @computed get scopeStoreId (): number {
    if (!this.scopeBrand) return '' as any
    return this.scopeBrand.id
  }

  @computed get storeDropdownOptions (): SelectOption<number, ImageLabelLineProps>[] {
    const { brandsList } = this._rootStore[BRAND_STORE]
    if (!brandsList || !brandsList.length) {
      if (!this.scopeBrand) return []
      return [{
        label: this.scopeBrand.name,
        value: this.scopeBrand.id,
        customComponentProps: {
          imgSrc: getMainImageSrcForObj(this.scopeBrand),
          label: this.scopeBrand.name
        }
      }]
    }

    return brandsList.map(brand => ({
      label: brand.name,
      value: brand.id,
      customComponentProps: {
        imgSrc: getMainImageSrcForObj(brand),
        label: brand.name
      }
    }))
  }

  @computed get productsOptionList (): ObjectOption[] {
    return this._parseToObjectOptions(this._brandProducts)
  }

  @computed get selectedProductsList (): ObjectOption[] {
    return this._parseToObjectOptions(this.scopeProducts)
  }

  @computed get projectOptionList (): MultiSelectOption<any>[] {
    return this._projectsList.map((project) => {
      console.log(project.children)
      return {
        label: project.name,
        value: project.id,
        children: project.children.map(p =>
          ({ label: p.name, value: p.id, children: p.children.map(pj => ({ label: pj.name, value: pj.id })) } as any)),
        // options: project.children.map(p =>
        //   ({ label: p.name, value: p.id, children: p.children.map(pj => ({ label: pj.name, value: pj.id })), options: p.children.map(pj => ({ label: pj.name, value: pj.id })) } as any)),
        versionChange: this.getChangeByKeyId(`project-${project.id}`)
      }
    })
  }

  @computed get selectedProjectIds (): number[] {
    console.log(toJS(this.scopeProjects))

    return this.scopeProjects.map(p => p.id)
  }

  @computed get period (): OnSelectCallbackParam {
    if (!this.agreement) return null

    const {
      startDate,
      endDate
    } = this.agreement
    if (!startDate || !endDate) return null

    return moment.range(moment(startDate), moment(endDate))
  }

  @computed get periodString (): string {
    if (!this.period) return ''

    return `
    ${this.period.start.format('DD/MM/YYYY')
      } - ${this.period.end.format('DD/MM/YYYY')}
    `
  }

  @computed get agreementStatus (): AgreementStatusTypes {
    if (!this.agreement) return AgreementStatusTypes.NOT_ACTIVE

    return this.agreement.agreementStatus
  }

  @computed get scopeProductsCount (): number {
    return this.scopeProducts.length
  }

  @computed get scopeProjectsCount (): number {
    return this.scopeProjects.length
  }

  @computed get hasPrevVersions (): boolean {
    return Boolean(this.agreement?.previous)
  }

  @computed get prevVersions (): AgreementDTO[] {
    if (!this.hasPrevVersions) return []

    const prevVersions: AgreementDTO[] = []

    const pushPrevVersionToArr = (prevVersion: AgreementDTO) => {
      prevVersions.push(prevVersion)
      if (prevVersion.previous) pushPrevVersionToArr(prevVersion.previous)
    }

    pushPrevVersionToArr(this.agreement.previous)

    return prevVersions
  }

  @computed get isAgreementPendingApproval (): boolean {
    if (User.isManager()) {
      return this.agreementStatus === AgreementStatusTypes.PENDING_PROJECT_APPROVAL
    }
    if (User.isShopOwner()) {
      return this.agreementStatus === AgreementStatusTypes.PENDING_STORE_APPROVAL
    }
    return [
      AgreementStatusTypes.PENDING_PROJECT_APPROVAL,
      AgreementStatusTypes.PENDING_STORE_APPROVAL
    ].includes(this.agreementStatus)
  }

  @computed get changes (): VersionChange[] {
    if (
      !this.isAgreementPendingApproval ||
      !this.hasPrevVersions ||
      this.areAllChangesAccepted
    ) return []

    const agreementAfterChange: AgreementDTO = this.agreement
    const agreementBeforeChange: AgreementDTO = this.agreement.previous

    const result = new DeepDiffMapper()
      .map<AgreementDTO>(agreementBeforeChange, agreementAfterChange) as DiffMapDeepResult<AgreementDTO>

    const changes: VersionChange[] = []

    const startDateChange = result.startDate as ObjectValueDiff
    const endDateChange = result.endDate as ObjectValueDiff
    const periodChange: ObjectValueDiff = {
      beforeChange: (startDateChange?.beforeChange || endDateChange?.beforeChange)
        ? `${moment(startDateChange?.beforeChange || this.agreement.startDate).format('DD.MM.YY')
        } - ${moment(endDateChange?.beforeChange || this.agreement.endDate).format('DD.MM.YY')}`
        : undefined,
      afterChange: (startDateChange?.afterChange || endDateChange?.afterChange)
        ? `${moment(startDateChange?.afterChange || this.agreement.startDate).format('DD.MM.YY')
        } - ${moment(endDateChange?.afterChange || this.agreement.endDate).format('DD.MM.YY')}`
        : undefined,
      status: startDateChange?.status || endDateChange?.status
    }

    Object.entries(result)
      .forEach((data) => {
        const key = data[0] as keyof AgreementDTO
        if (this.versionChangeKeyIdsToSkip.includes(key)) return
        const value = data[1]
        const { status, beforeChange, afterChange } = value as ObjectValueDiff
        const propLabel = getAgreementPropLabel(key)

        const changeObjectPredefined: Partial<VersionChange> = {
          markLetter: '',
          keyId: key
        }

        switch (key) {
          case 'name': {
            changes.push({
              ...changeObjectPredefined,
              message: `${capitalize(status)} ${propLabel.label} from "${beforeChange}" to "${afterChange}"`,
              valueBeforeChange: beforeChange,
              valueAfterChange: afterChange,
              valueStatus: status
            })
            break
          }
          case 'budget': {
            const message: string = {
              [ObjectValueStatus.ADDED]: `${capitalize(status)} ${propLabel.label}`,
              [ObjectValueStatus.CHANGED]: `${capitalize(status)} ${propLabel.label
                } from ${beforeChange} ${propLabel.unit} to ${afterChange} ${propLabel.unit}`
            }[status]
            changes.push({
              ...changeObjectPredefined,
              message,
              valueBeforeChange: beforeChange,
              valueAfterChange: afterChange,
              valueStatus: status
            })
            break
          }
          case 'budgetBuffer': {
            const message: string = {
              [ObjectValueStatus.ADDED]: `${capitalize(status)} ${propLabel.label}`,
              [ObjectValueStatus.CHANGED]: `${capitalize(status)} ${propLabel.label
                } from ${beforeChange} ${propLabel.unit} to ${afterChange} ${propLabel.unit}`
            }[status]
            changes.push({
              ...changeObjectPredefined,
              message,
              valueBeforeChange: beforeChange,
              valueAfterChange: afterChange,
              valueStatus: status
            })
            break
          }
          case 'startDate':
          case 'endDate': {
            if (changes.find(change => change.keyId === 'period')) break
            const message: string = {
              [ObjectValueStatus.ADDED]: `${capitalize(status)} ${propLabel.label}`,
              [ObjectValueStatus.CHANGED]: `${capitalize(status)} ${propLabel.label
                } from ${periodChange?.beforeChange} to ${periodChange?.afterChange}`
            }[status]
            if (this.versionChangeKeyIdsToSkip.includes('period')) {
              changes.push({
                ...changeObjectPredefined,
                keyId: 'period',
                message,
                valueBeforeChange: periodChange?.beforeChange,
                valueAfterChange: periodChange?.afterChange,
                valueStatus: status
              })
            }
            break
          }
          case 'items': {
            let itemProperties: { [key in keyof AgreementItemPropertyTypes]?: ObjectValueDiff } = {}
            Object.values(value).forEach(item => {
              if (item.properties) {
                itemProperties = {
                  ...itemProperties,
                  ...item.properties
                }
              }
            })
            Object.entries(itemProperties)
              .forEach(([propType, diffObj]) => {
                const { status, beforeChange, afterChange } = diffObj
                const propTypeLabel = getAgreementPropLabel(propType as AgreementItemPropertyTypes)
                const message: string = {
                  [ObjectValueStatus.ADDED]: `${capitalize(status)} ${propTypeLabel.label}`,
                  [ObjectValueStatus.CHANGED]: `${capitalize(status)} ${propTypeLabel.label
                    } from ${beforeChange} ${propTypeLabel.unit} to ${afterChange} ${propTypeLabel.unit}`
                }[status]
                if (this.versionChangeKeyIdsToSkip.includes(propType)) {
                  changes.push({
                    markLetter: '',
                    keyId: propType,
                    message,
                    valueBeforeChange: beforeChange,
                    valueAfterChange: afterChange,
                    valueStatus: status
                  })
                }
              })
            break
          }
          case 'scopes': {
            const scopeFilters = Object.values(value[0].filters)
            const generateDiffObject = (entity: any): ObjectValueDiff => {
              if (!entity) return undefined
              return {
                status: entity.id?.status || entity.status,
                beforeChange: entity.id?.beforeChange
                  ? {
                    id: entity.id?.beforeChange,
                    name: entity.name?.beforeChange
                  }
                  : entity.beforeChange || undefined,
                afterChange: entity.id?.afterChange
                  ? {
                    id: entity.id?.afterChange,
                    name: entity.name?.afterChange
                  }
                  : entity.afterChange || undefined
              }
            }

            const storeFilter = scopeFilters.find((f: any) => f.brands && JSON.stringify(f.brands) !== '{}') as any || {}
            const storeDiff: ObjectValueDiff = generateDiffObject(storeFilter.brands?.[0])

            const projectsFilter = scopeFilters.find((f: any) => f.projects && JSON.stringify(f.projects) !== '{}') as any || {}
            const projectDiffs: ObjectValueDiff[] = Object.values(projectsFilter.projects || {}).map(generateDiffObject)
            const productsFilter = scopeFilters.find((f: any) => f.products && JSON.stringify(f.products) !== '{}') as any || {}
            const productDiffs: ObjectValueDiff[] = Object.values(productsFilter.products || {}).map(generateDiffObject)

            if (storeDiff) {
              const message: string = {
                [ObjectValueStatus.ADDED]: `${capitalize(storeDiff.status)} store "${storeDiff.afterChange?.name}"`,
                [ObjectValueStatus.CHANGED]: `${capitalize(storeDiff.status)
                  } store from "${storeDiff.beforeChange?.name}" to "${storeDiff.afterChange?.name}"`
              }[storeDiff.status]
              if (!this.versionChangeKeyIdsToSkip.includes('store')) {
                changes.push({
                  markLetter: '',
                  keyId: 'store',
                  message,
                  valueBeforeChange: storeDiff.beforeChange,
                  valueAfterChange: storeDiff.afterChange,
                  valueStatus: storeDiff.status
                })
              }
            }

            if (projectDiffs && projectDiffs.length) {
              projectDiffs.forEach(({ status, beforeChange, afterChange }) => {
                const message: string = {
                  [ObjectValueStatus.ADDED]: `${capitalize(status)} project "${afterChange?.name}"`,
                  [ObjectValueStatus.REMOVED]: `${capitalize(status)} project "${beforeChange?.name}"`,
                  [ObjectValueStatus.CHANGED]: `${capitalize(status)
                    } project from "${beforeChange?.name}" to "${afterChange?.name}"`
                }[status]
                const projectKeyId = `project-${beforeChange?.id}-${afterChange?.id}`
                if (!this.versionChangeKeyIdsToSkip.includes(projectKeyId)) {
                  changes.push({
                    markLetter: '',
                    keyId: projectKeyId,
                    message,
                    valueBeforeChange: beforeChange,
                    valueAfterChange: afterChange,
                    valueStatus: status
                  })
                }
              })
            }

            if (productDiffs && productDiffs.length) {
              productDiffs.forEach(({ status, beforeChange, afterChange }) => {
                const message: string = {
                  [ObjectValueStatus.ADDED]: `${capitalize(status)} product "${afterChange?.name}"`,
                  [ObjectValueStatus.REMOVED]: `${capitalize(status)} product "${beforeChange?.name}"`,
                  [ObjectValueStatus.CHANGED]: `${capitalize(status)
                    } product from "${beforeChange?.name}" to "${afterChange?.name}"`
                }[status]
                const productKeyId = `product-${beforeChange?.id}-${afterChange?.id}`
                if (!this.versionChangeKeyIdsToSkip.includes(productKeyId)) {
                  changes.push({
                    markLetter: '',
                    keyId: productKeyId,
                    message,
                    valueBeforeChange: beforeChange,
                    valueAfterChange: afterChange,
                    valueStatus: status
                  })
                }
              })
            }

            break
          }
        }
      })

    changes.forEach((change, index) => {
      change.markLetter = String.fromCharCode(65 + index)
    })

    return changes
  }

  @computed get changesCount (): number {
    return this.changes.length
  }

  @computed get hasVersionChanges (): boolean {
    return Boolean(this.changesCount)
  }

  @computed private get projectChanges (): VersionChange[] {
    return this.changes.filter(change => change.keyId.startsWith('project'))
  }

  @computed private get productChanges (): VersionChange[] {
    return this.changes.filter(change => change.keyId.startsWith('product'))
  }

  @observable getChangeByKeyId = computedFn(
    (keyId: string): VersionChange => {
      if (keyId.startsWith('project')) {
        const projectId = keyId.split('-')[1]
        const changeObj = this.projectChanges.find(change => {
          return change.keyId.split('-').includes(projectId)
        })
        return changeObj
      }
      if (keyId.startsWith('product')) {
        const productId = keyId.split('-')[1]
        const changeObj = this.productChanges.find(change => {
          return change.keyId.split('-').includes(productId)
        })
        return changeObj
      }
      return this.changes.find(change => change.keyId === keyId)
    }
  )
}
